import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

import { FormHandler, Textarea } from 'Components/Form';
import { Card, CardBody } from 'Components/Card';

import ChatMessage from './ChatMessage';

import { Toast, Socket, Event } from 'Services';
import { translation } from 'Services/TranslationHelpers';
import ChatApi from 'Services/Api/MainStage/Chat';

class Chat extends React.Component {
    /**
     * @var containerRef
     * @type {null|HTMLDivElement}
     */
    containerRef = null;

    /**
     * @var titleRef
     * @type {null|HTMLDivElement}
     */
    titleRef = null;

    /**
     * @var formRef
     * @type {null|HTMLDivElement}
     */
    formRef = null;

    /**
     * @var state
     * @type {{messages: [], meta: null, working: boolean}}
     */
    state = {
        working: false,
        messages: [],
        meta: null,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.fetchMessages();

        this.containerRef.addEventListener('scroll', this.handleLoadMore);

        Socket.getConnection()
            .private(`main_stage_chat.${this.props.sessionId}`)
            .listen('MainStageChat', (e) => {
                if (e.message.main_stage_chat_id === null) {
                    this.handleAddMessage(e.message);
                }
            })
            .listen('MainStageChatDeleted', (e) => this.handleDeleteMessage(e.message))
            .listen('subscription_error', console.error);

        if (window.base.main_stage.layout === 'tabs') {
            this.setSize();
        }
    };

    /**
     * @method componentDidUpdate
     * @param {object} prevProps
     */
    componentDidUpdate = (prevProps) => {
        if (window.base.main_stage.layout === 'tabs' && prevProps.height !== this.props.height) {
            this.setSize();
        }
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        this.containerRef.removeEventListener('scroll', this.handleLoadMore);

        Socket.getConnection().leave(`main_stage_chat.${this.props.sessionId}`);
    };

    /**
     * @method setSize
     */
    setSize = () => {
        this.containerRef.style.height = `${this.props.height - this.formRef.clientHeight - this.titleRef.clientHeight - 48}px`;
    };

    /**
     * @method fetchMessages
     * @return {Promise<void>}
     */
    fetchMessages = async () => {
        if (this.state.working || (this.state.meta !== null && this.state.meta?.current_page === this.state.meta?.last_page)) {
            return;
        }

        this.setState({ working: true });

        const request = await ChatApi.get({
            session: this.props.sessionId,
        }, {
            page: (this.state.meta?.current_page ?? 0) + 1,
        });

        if (request.success) {
            return this.setState({
                messages: [...this.state.messages, ...request.data.data],
                meta: request.data.meta,
                working: false,
            });
        }

        Toast.error();
    };

    /**
     * @method handleAddMessage
     * @param {object} message
     */
    handleAddMessage = (message) => {
        const i = this.state.messages.map(m => m.id).indexOf(message.id);

        if (i === -1) {
            this.setState({
                messages: [
                    ...[message],
                    ...this.state.messages,
                ],
            });
        }
    };

    /**
     * @method handleAddMessage
     * @param {object} message
     */
    handleDeleteMessage = (message) => {
        this.setState({
            messages: this.state.messages.filter(m => m.id !== message.id),
        });
    };

    /**
     * @methid handleLoadMore
     * @param {Event} e
     */
    handleLoadMore = (e) => {
        const bottom = e.target.scrollHeight - Math.ceil(e.target.scrollTop) === e.target.clientHeight;

        if (bottom) {
            this.fetchMessages();
        }
    };

    /**
     * @method handlePostMessage
     * @param {object} data
     * @return {Promise<*>}
     */
    handlePostMessage = (data) => {
        return ChatApi.post({
            session: this.props.sessionId
        }, {
            ...data,
            session_id: this.props.sessionId
        });
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const { messages, working } = this.state;
        const { bgColour = 'bg-app-gray' } = this.props;

        return (
            <Card className={`h-full ${bgColour} text-app-blue rounded-b-xl`} bgColour="bg-app-blue" titleColor="text-white">
                <CardBody className="h-full flex flex-col">
                    <h1 className="text-2xl font-bold" ref={ref => this.titleRef = ref}></h1>

                    <div
                        className={`flex-1 overflow-y-auto py-2 ${window.base.main_stage.layout === 'tabs' ? '' : 'max-h-96 h-full'}`}
                        ref={ref => this.containerRef = ref}>
                        {(!messages || messages.length === 0) && (
                            <p className="text-app-blue mt-4">
                                {translation('main_stage_frontend', 'post_a_comment')}
                            </p>
                        )}

                        {messages && messages.map((message, i) => (
                            <ChatMessage
                                message={message}
                                sessionId={this.props.sessionId}
                                key={message.id}
                            />
                        ))}

                        {working && (
                            <div className="py-8 text-center text-white">
                                <FontAwesomeIcon icon={faSpinner} spin size="2x"/>
                            </div>
                        )}
                    </div>

                    {this.renderNewMessageForm()}
                </CardBody>
            </Card>
        );
    }

    /**
     * @method renderNewMessageForm
     * @return {JSX.Element}
     */
    renderNewMessageForm = () => {
        const { form, working, handleInput, handleSubmit } = this.props;

        return (
            <div className="relative w-full" ref={ref => this.formRef = ref}>
                <Textarea
                    rows={3}
                    value={form.message ?? ''}
                    onChange={(v) => handleInput('message', v)}
                    placeholder={translation('misc', 'type_here')}
                    emojiPicker={true}
                    className="text-app-blue"
                />

                <FontAwesomeIcon
                    icon={faPaperPlane}
                    size="1x"
                    onClick={() => !working && form.message ? handleSubmit(
                        null,
                        this.handlePostMessage,
                        (message) => this.handleAddMessage(message.data.data),
                        true,
                    ) : false}
                    className="absolute top-12 right-6 text-black cursor-pointer"
                />
            </div>
        );
    };
}

export default FormHandler(Chat);
